import store from '@/store.js'
import api from '@/api/api'
import StatusBL from './statusBl'
import Vue from 'vue'

export default {
  getCandidates () {
    return store.getters['getCandidates']
  },

  getAllQuestions () {
    return store.getters['getQuestions']
  },

  getAllPages () {
    return store.getters['getPages'].sort((a, b) => a.prio - b.prio)
  },

  getAllCategories () {
    return store.getters['getCategories'].sort((a, b) => a.prio - b.prio)
  },

  getDSBVersion () {
    return store.getters['getDSBVersion']
  },

  getDSB () {
    return store.getters['getDSB']
  },

  getPermissions () {
    return store.getters['getPermissions']
  },

  candidateHasAcceptedDSB (candidate) {
    return candidate && candidate.metadata && candidate.metadata.dsb_accepted_version
      ? candidate.metadata.dsb_accepted_version === this.getDSBVersion()
      : false
  },

  questionMeetsConditions (question, answers) {
    if (!question) {
      return false
    }

    if (!question.metadata || !question.metadata.show_only_if_function) {
      return true
    }

    answers = answers || []
    const body = question.metadata.show_only_if_function
    const answersHashMap = answers.reduce((acc, val) => {
      acc[val.question_key] = val.answer
      return acc
    }, {})

    const permissions = Object.assign({}, this.getPermissions())
    const fnStr = 'fn = function (answers, permissions) { ' + body + '; };'
    let fn

    try {
      // eslint-disable-next-line no-eval
      eval(fnStr)
      const result = fn(answersHashMap, permissions)
      return result
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('condition function error', e, fnStr)
      return false
    }
  },

  canShowQuestion (question, answers) {
    switch (true) {
      case !question:
      case question.metadata && question.metadata.activated === false:
      case !this.questionMeetsConditions(question, answers):
        return false
    }

    return true
  },

  getQuestionsToRender (answers) {
    const questions = this.getAllQuestions()

    return questions
      ? questions.filter(q => this.canShowQuestion(q, answers))
      : []
  },

  getPagesToRender (answers) {
    const pages = this.getAllPages()

    return pages
      ? pages.filter(p => !!this.getQuestionsToRenderForPage(p, answers).length)
      : []
  },

  getCategoriesToRender (answers) {
    const categories = this.getAllCategories()

    return categories
      ? categories.filter(c => !!this.getQuestionsToRenderForCategory(c, answers).length)
      : []
  },

  getQuestionsToRenderForPage (page, answers) {
    return this.getQuestionsToRender(answers).filter(q => q.page === page.key)
  },

  getQuestionsToRenderForCategory (category, answers) {
    return this.getQuestionsToRender(answers).filter(q => q.category === category.key)
  },

  getAnswerForQuestion (question, answers) {
    return answers.find(a => a.question_key === question.key)
  },

  getAnswerForQuestionKey (questionKey, answers) {
    return answers.find(a => a.question_key === questionKey)
  },

  getAnswerForFormLabel (formLabel, answers) {
    const questions = this.getAllQuestions()
      .filter(q => q.form_label === formLabel)
    const questionKeyHash = questions.reduce((hash, q) => {
      hash[q.key] = true
      return hash
    }, {})
    return answers.find(a => questionKeyHash[a.question_key])
  },

  getMandatoryQuestionsWithoutVerification (questions, answers) {
    return questions
      ? questions.filter(q => {
        if (!q.metadata || !q.metadata.verfication_required_for_finalize) {
          return false
        }

        const answer = this.getAnswerForQuestion(q, answers)
        return answer
          ? answer.verification.status !== true
          : true
      })
      : []
  },

  getMandatoryQuestionsWithoutAnswers (questions, answers) {
    return questions
      ? questions.filter(q => {
        if (!q.metadata || !q.metadata.mandatory) {
          return false
        }

        const answer = this.getAnswerForQuestion(q, answers)
        return answer
          ? answer.answer === void 0
          : true
      })
      : []
  },

  canEditCandidate (candidate) {
    return candidate
      ? !candidate.metadata.employee_id
      : true
  },

  canPromoteCandidate (candidate, answers, status) {
    return store.getters['hasPermissionToPromoteCandidate'] && !!candidate && !candidate.metadata.employee_id && !!status && status.value === StatusBL.states.STATUS_RECRUITED
  },

  updateAnswerWithValue (question, answer, value, questions, answers, { preventAnswerCleanup = false } = {}) {
    if (answer) {
      Vue.set(answer, 'answer', value) // answer.answer = value
    } else {
      answer = this.createAnswerWithValue(question, value)
      answers.push(answer)
    }

    if (!preventAnswerCleanup) {
      this._cleanUpAnswers(questions, answers)
    }
  },

  updateAnswerVerification (question, answer, status, comment, answers) {
    if (!answer) {
      answer = this.createAnswerWithValue(question, void 0)
      answers.push(answer)
    }

    const user = store.getters['getUser']

    const verification = {
      status: status || false,
      comment: comment || void 0,
      timestamp: Date.now() / 1000,
      verification_customer: user.hierarchy_id
    }

    answer.verification = verification
  },

  // Remove deprecated answers
  // (answers from questions that do not meet their conditions anymore)
  _cleanUpAnswers (questions, answers) {
    questions.forEach(question => {
      if (!this.questionMeetsConditions(question, answers)) {
        const index = answers.findIndex(a => a.question_key === question.key)
        if (index > -1) {
          answers.splice(index, 1)
        }
      }
    })
  },

  createAnswerWithValue (question, value) {
    return {
      question_key: question.key,
      answer: value,
      verification: {
        status: false,
        verification_officer: void 0,
        verification_customer: void 0,
        timestamp: void 0,
        comment: void 0
      }
    }
  },

  updateCandidateDSB (candidate, hasDSB) {
    if (!candidate) {
      return
    }

    store.commit('setCandidateDSB', {
      candidate: candidate,
      value: hasDSB ? this.getDSBVersion() : void 0
    })
  },

  /// ////////////////////////////////////////////////
  // API Glue       /////////////////////////////////
  /// ////////////////////////////////////////////////

  requestInitial () {
    return api.getInitialData()
      .then(response => {
        store.commit('setPermissions', {
          permissions: response.data.permissions
        })
        store.commit('setUser', {
          user: response.data.user
        })
        store.commit('setDSBVersion', {
          dsb_version: response.data.dsb
        })
      })
  },

  requestCandidates (status) {
    return api.getCandidates(status)
      .then(response => {
        store.commit('setCandidates', {
          candidates: response.data.results
        })
        store.commit('setMetaInfos', response.data.serverData)
      })
  },

  requestCandidateCount () {
    return api.getCandidateCount()
      .then(response => {
        store.commit('setCandidateCount', {
          wartelisteCount: response.data.countWarteliste,
          inboxCount: response.data.countActivated,
          eingestelltCount: response.data.countEingestellt,
          abgesagtCount: response.data.countAbgesagt,
          verifiedCount: response.data.countVerified,
          allCount: response.data.countAlle
        })
      })
  },

  requestQuestions () {
    return api.getQuestions()
      .then(response => {
        store.commit('setQuestions', {
          questions: response.data
        })
      })
  },

  requestPages () {
    return api.getPages()
      .then(response => {
        store.commit('setPages', {
          pages: response.data
        })
      })
  },

  requestCategories () {
    return api.getCategories()
      .then(response => {
        store.commit('setCategories', {
          categories: response.data
        })
      })
  },

  requestDSB () {
    if (store.getters['getDSB']) {
      return new Promise(resolve => resolve())
    }
    return api.getDSB()
      .then(response => {
        store.commit('setDSB', {
          dsb: response.data.privacy
        })
      })
  },

  requestEssential () {
    return this.requestInitial()
      .then(_ => {
        const shouldGetCandidates = store.getters['hasPermissionToViewCandidatesList']

        return shouldGetCandidates
          ? Promise.all([
            this.requestCandidates(),
            this.requestCandidateCount(),
            this.requestPages(),
            this.requestCategories(),
            this.requestQuestions()
          ])
          : Promise.all([
            this.requestPages(),
            this.requestQuestions()
          ])
      })
  },

  createCandidateAndSaveWithAnswers (answers) {
    const user = store.getters['getUser']
    const candidateUUID = store.getters['getCandidateUUID']
    const candidate = {
      uuid: candidateUUID,
      answers: JSON.parse(JSON.stringify(answers)),
      metadata: {
        status: 'new',
        creator: user.user_id,
        creation_timestamp: void 0,
        customer: 20186, // ACHTUNG: NUR VIVANTES!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! // user.hierarchy_id,
        dsb_accepted_version: this.getDSBVersion(),
        activated: true
      }
    }

    return api.createCandidate(candidate)
      .then(_ => {
        store.commit('addCandidate', {
          candidate: candidate
        })
        store.commit('clearCandidateUUID')
      })
  },

  upsertCandidateAndSaveWithAnswers (answers) {
    const user = store.getters['getUser']
    const candidateUUID = store.getters['getCandidateUUID']
    const candidate = {
      uuid: candidateUUID,
      answers: JSON.parse(JSON.stringify(answers)),
      metadata: {
        status: 'new',
        creator: user.user_id,
        creation_timestamp: void 0,
        customer: 20186, // ACHTUNG: NUR VIVANTES!
        dsb_accepted_version: this.getDSBVersion(),
        activated: true
      }
    }

    return api.upsertCandidate(candidate)
      .then(_ => {
        store.commit('addCandidate', {
          candidate: candidate
        })
        store.commit('clearCandidateUUID')
      })
  },

  saveAnswersForCandidate (answers, candidate) {
    candidate.answers = JSON.parse(JSON.stringify(answers))

    return api.upsertCandidate(candidate)
      .then(_ => {
        store.commit('updateAnswersForCandidate', {
          candidate: candidate,
          answers: answers
        })
        return this.requestCandidateCount()
      })
  },

  exportCandidate (candidate, mail) {
    return api.exportCandidate({
      uuid: candidate.uuid,
      mail: mail
    })
  },

  sendRejectionMail (candidateUuid) {
    return api.sendRejectionMail(candidateUuid)
  },

  createEmployee (candidate, answers) {
    candidate.answers = JSON.parse(JSON.stringify(answers))
    return api
      .upsertCandidate(candidate)
      .then(_ => {
        return api
          .createEmployee({
            uuid: candidate.uuid
          })
          .then(response => {
            store.commit('updateCandidateMetadata', response.data.employee)
          })
      })
  }
}
